/**
 * Upload event logger. Fires a POST /api/upload-events for each stage in the
 * upload funnel so admin can see attempts-vs-completions and get alerted on
 * failures / stuck sessions.
 *
 * Contract with the backend (see `POST /api/upload-events` in server.py):
 *   stage        → one of the five stages below
 *   session_id   → uuid minted client-side per attempt (a "session"). All
 *                  events for the same upload attempt share the same id so
 *                  the funnel can dedupe attempts from retries.
 *   video_id     → set once finalize returns
 *   file_metadata→ {name, size, type} — sent with file_selected + initiated
 *   error_type   → coarse category (client_network, server_5xx, etc)
 *   error_message→ raw message string, truncated by backend
 *
 * IMPORTANT: never AWAIT this call in the hot path — the analytics event
 * MUST NOT block the actual upload flow. Errors from the log endpoint are
 * silently caught. The upload itself is the source of truth; this is just
 * observability data.
 */

export const UPLOAD_STAGES = {
  ATTEMPT_STARTED: 'attempt_started',
  FILE_SELECTED: 'file_selected',
  UPLOAD_INITIATED: 'upload_initiated',
  UPLOAD_COMPLETED: 'upload_completed',
  UPLOAD_FAILED: 'upload_failed',
};

// Mint a new session id when the user opens the upload flow. Retry attempts
// on the SAME opened upload panel share the same id — closing/reopening
// mints a new one.
export function newUploadSessionId() {
  // crypto.randomUUID exists in every modern browser + Safari 15.4+.
  // Fallback to a timestamp-based id for very old browsers so we don't
  // throw during the log call.
  try {
    return typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  } catch {
    return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Log a single upload-flow event. Fire-and-forget: never throws, never
 * blocks. The `axiosInstance` param is passed in so this util can be used
 * anywhere without importing global state.
 *
 * @param {object}  args
 * @param {object}  args.axiosInstance   Configured axios (with auth header)
 * @param {string}  args.stage           One of UPLOAD_STAGES.*
 * @param {string}  args.sessionId       uuid, mint via newUploadSessionId()
 * @param {string=} args.videoId         Set after finalize returns
 * @param {object=} args.fileMetadata    {name, size, type}
 * @param {string=} args.errorType       Coarse category (with FAILED)
 * @param {string=} args.errorMessage    Raw error text (with FAILED)
 */
export function logUploadEvent({
  axiosInstance,
  stage,
  sessionId,
  videoId,
  fileMetadata,
  errorType,
  errorMessage,
}) {
  if (!axiosInstance || !stage || !sessionId) return;
  const body = { stage, session_id: sessionId };
  if (videoId) body.video_id = videoId;
  if (fileMetadata) {
    // Coerce to plain object so File objects (which spread weirdly) don't
    // send their internal state.
    body.file_metadata = {
      name: fileMetadata.name || '',
      size: Number(fileMetadata.size) || 0,
      type: fileMetadata.type || '',
    };
  }
  if (errorType) body.error_type = errorType;
  if (errorMessage) body.error_message = String(errorMessage).slice(0, 1000);
  // Fire-and-forget. Any failure here is invisible to the user — we only
  // log to console so devs debugging observability drops can see them.
  axiosInstance.post('/upload-events', body).catch((err) => {
    // Intentionally silent to the user. This is telemetry; if it drops,
    // the actual upload still works.
    console.warn('[uploadEvents] log failed', stage, err?.message || err);
  });
}
